// Feature: live permit board (owner: shell work package).
// Contract: export BoardPage.
//
// Live and suspended permits by area (GOV-038), each with the server's work
// eligibility as evaluated at read time. Refreshes every 30 seconds; a board that
// could not refresh says so, because a stale board is not a current authorisation
// (SYS-009).
import { useEffect, useId, useState } from 'react'
import { Link } from 'react-router-dom'
import { blockerText, EligibilityBadge, ErrorBanner, RuleRefs } from '@/components/evidence'
import { Badge, Banner, Button, cx, EmptyState, Field, PageHeader, Select, Spinner } from '@/components/ui'
import type { BoardEntry, PersonRef } from '@/domain/types'
import { fmtDateTime } from '@/lib/format'
import { useBoard } from '@/lib/queries'
import { cycleStateInfo, permitStateInfo, plural } from '@/features/home/permitList'
import { areaOptions, boardAlert, boardCounts, boardExtension, boardOmApproval, firstBlocker, groupBoardByArea, validationWindowText, validatorLabel } from './boardModel'

const REFRESH_SECONDS = 30
/** Older than three missed refreshes: say so. */
const STALE_AFTER_MS = 3 * REFRESH_SECONDS * 1000

function useNow(intervalMs: number): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), intervalMs)
    return () => window.clearInterval(t)
  }, [intervalMs])
  return now
}

function personText(p: PersonRef | null): string {
  if (!p) return 'Not named yet'
  return p.organisation ? `${p.display_name} (${p.organisation})` : p.display_name
}

export function BoardPage() {
  const q = useBoard()
  const [areaId, setAreaId] = useState('')
  const now = useNow(15_000)
  const areaFieldId = useId()

  const entries = q.data ?? []
  const areas = areaOptions(entries)
  const area = areas.some((a) => a.id === areaId) ? areaId : ''
  const groups = groupBoardByArea(entries, area)
  const counts = boardCounts(area ? entries.filter((e) => e.site_area.id === area) : entries)
  const loadedAt = q.dataUpdatedAt ? new Date(q.dataUpdatedAt).toISOString() : null
  const stale = Boolean(q.data) && (q.isError || now - q.dataUpdatedAt > STALE_AFTER_MS)

  return (
    <div className="space-y-4">
      <PageHeader
        title="Live board"
        subtitle="Authorised permits by unit, with each permit's status as the server sees it now."
        actions={
          <Button variant="secondary" onClick={() => void q.refetch()} disabled={q.isFetching}>
            {q.isFetching ? 'Refreshing...' : 'Refresh now'}
          </Button>
        }
      />
      <p className="text-sm text-ink-soft" aria-live="polite">
        Refreshes every {REFRESH_SECONDS} seconds.{' '}
        {loadedAt ? `Last loaded ${fmtDateTime(loadedAt)} (this device's clock).` : null}
      </p>

      {q.isLoading ? (
        <div className="flex justify-center p-6">
          <Spinner />
        </div>
      ) : !q.data ? (
        <ErrorBanner error={q.error ?? new Error('The board is not available.')} title="Could not load the board" />
      ) : (
        <>
          {stale ? (
            <Banner tone="warn" title="This board may be out of date">
              {q.isError ? 'The last refresh failed. ' : ''}It shows the permits as loaded at {fmtDateTime(loadedAt)}. A stale board
              is not a current authorisation: refresh, or open the permit, before relying on it.
              <RuleRefs ruleIds={['SYS-009']} />
            </Banner>
          ) : null}

          <div className="flex flex-wrap items-end justify-between gap-x-4">
            <div className="w-full sm:w-80">
              <Field label="Unit" htmlFor={areaFieldId}>
                <Select
                  id={areaFieldId}
                  value={area}
                  onChange={(e) => setAreaId(e.target.value)}
                  options={areas.map((a) => ({ value: a.id, label: `${a.name} (${a.code})` }))}
                  placeholder="All units"
                />
              </Field>
            </div>
            <ul className="mb-4 flex flex-wrap gap-2" aria-label="Board summary">
              <li>
                <Badge tone="neutral">{plural(counts.total, 'permit')} on the board</Badge>
              </li>
              {counts.stops > 0 ? (
                <li>
                  <Badge tone="bad">{plural(counts.stops, 'stop')} reported</Badge>
                </li>
              ) : null}
              {counts.suspended > 0 ? (
                <li>
                  <Badge tone="warn">{counts.suspended} suspended</Badge>
                </li>
              ) : null}
              <li>
                <Badge tone={counts.mayProceed > 0 ? 'ok' : 'neutral'}>{counts.mayProceed} where work may proceed</Badge>
              </li>
            </ul>
          </div>

          {groups.length === 0 ? (
            <EmptyState title={area ? 'No live permits in this area' : 'No live permits'}>
              Authorised permits appear here with their daily status.
            </EmptyState>
          ) : (
            <div className="space-y-6">
              {groups.map((g) => (
                <section key={g.area.id} aria-labelledby={`area-${g.area.id}`}>
                  <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2 border-b border-line pb-1">
                    <h2 id={`area-${g.area.id}`} className="display text-lg font-semibold text-ink">
                      {g.area.name} <span className="code whitespace-nowrap text-xs font-normal text-ink-faint">{g.area.code}</span>
                    </h2>
                    <p className="text-xs text-ink-soft">
                      {plural(g.entries.length, 'permit')}
                      {g.stops > 0 ? ` - ${plural(g.stops, 'stop')} reported` : ''}
                      {g.suspended > 0 ? ` - ${g.suspended} suspended` : ''}
                    </p>
                  </div>
                  <ul className="grid gap-3 md:grid-cols-2">
                    {g.entries.map((e) => (
                      <li key={e.permit_id}>
                        <BoardCard e={e} />
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          )}

          <p className="text-xs text-ink-soft">
            Isolations: none can be linked in this demonstration (isolation is not built yet).
            <RuleRefs ruleIds={['GOV-038']} />
          </p>
        </>
      )}
    </div>
  )
}

function AlertIcon({ stop }: { stop: boolean }) {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      {stop ? (
        <>
          <path d="M8 3h8l5 5v8l-5 5H8l-5-5V8z" />
          <path d="M12 8v5M12 16.5v.01" />
        </>
      ) : (
        <>
          <rect x="7" y="5" width="3.5" height="14" rx="1" />
          <rect x="13.5" y="5" width="3.5" height="14" rx="1" />
        </>
      )}
    </svg>
  )
}

function BoardCard({ e }: { e: BoardEntry }) {
  const alert = boardAlert(e)
  const fb = firstBlocker(e.eligibility)
  const windowText = validationWindowText(e.cycle?.validation_from, e.cycle?.validation_to)
  const classA = e.permit_class === 'A'
  const st = permitStateInfo(e.lifecycle_state)
  const om = boardOmApproval(e)
  const ext = boardExtension(e)
  return (
    <Link
      to={`/permits/${e.permit_id}`}
      className={cx(
        'block h-full rounded-xl border border-l-8 border-line p-3 shadow-sm transition hover:shadow-md',
        classA ? 'border-l-class-a' : 'border-l-class-b',
        alert?.tone === 'bad' ? 'bg-bad-light ring-2 ring-bad' : alert?.tone === 'warn' ? 'bg-warn-light ring-2 ring-warn/60' : 'bg-card',
      )}
    >
      {alert ? (
        <p className={cx('mb-2 flex items-center gap-1.5 text-sm font-bold uppercase tracking-wide', alert.tone === 'bad' ? 'text-bad' : 'text-warn')}>
          <AlertIcon stop={alert.rank === 0} />
          {alert.label}
        </p>
      ) : null}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="code font-semibold text-ink">{e.permit_number}</span>
        <span className={cx('text-xs font-bold uppercase tracking-wide', classA ? 'text-class-a' : 'text-class-b')}>Class {e.permit_class}</span>
        {e.lifecycle_state !== 'authorised' ? <Badge tone={st.tone}>{st.label}</Badge> : null}
        {om ? <Badge tone={om.tone}>{om.label}</Badge> : null}
        {ext.badge ? <Badge tone={ext.badge.tone}>{ext.badge.label}</Badge> : null}
      </div>
      <p className="mt-1 font-semibold text-ink">{e.title || 'Untitled'}</p>
      <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
        {ext.until ? (
          <>
            <dt className="text-ink-soft">Authorised to</dt>
            <dd className="text-ink">{ext.until}</dd>
          </>
        ) : null}
        <dt className="text-ink-soft">Cycle</dt>
        <dd className="text-ink">{e.cycle ? `#${e.cycle.cycle_number} ${cycleStateInfo(e.cycle.cycle_state).label}` : 'No cycle yet'}</dd>
        <dt className="text-ink-soft">Validation</dt>
        <dd className="text-ink">{windowText ?? 'Not validated'}</dd>
        <dt className="text-ink-soft">Permit Holder</dt>
        <dd className="text-ink">{personText(e.ph)}</dd>
        <dt className="text-ink-soft">{validatorLabel(e)}</dt>
        <dd className="text-ink">{e.aa ? personText(e.aa) : 'Not validated yet'}</dd>
      </dl>
      <div className="mt-2 border-t border-line pt-2">
        <div className="flex flex-wrap items-center gap-2">
          <EligibilityBadge e={e.eligibility} />
          {e.eligibility.status === 'unresolved' ? <span className="text-xs font-semibold text-warn">A rule or value is unresolved</span> : null}
        </div>
        {fb ? (
          <p className="mt-1 text-sm text-ink">
            {blockerText(fb.blocker)}
            <RuleRefs ruleIds={fb.blocker.rule_ids} decisionIds={fb.blocker.decision_ids} />
            {fb.more > 0 ? <span className="text-ink-soft"> (+{fb.more} more on the permit)</span> : null}
          </p>
        ) : null}
        <p className="mt-1 text-xs text-ink-faint">Checked by the server at {fmtDateTime(e.eligibility.evaluated_at)}</p>
      </div>
    </Link>
  )
}
